/**
 * ManageUsersPage.jsx — Tenant Admin: Manage Users
 * Accessible at /manage-users — admin role only.
 *
 * Features:
 *  - User list: create, edit, activate/deactivate, delete
 *  - Force Reset: admin sets a temporary password, user must change on next login
 *  - Security Settings: toggle between Admin-only and Self-Service (Email OTP) reset mode
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Plus, RefreshCw, Pencil, Trash2,
  KeyRound, Settings, ChevronDown, ChevronUp,
  ShieldCheck, Mail, RotateCcw
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import {
  listUsers, createUser, activateUser, deactivateUser, updateUser, deleteUser,
  forceResetUserPassword, getResetMode, setResetMode,
} from '../utils/api';
import './TenantAdminPage.css';

const EMPTY_FORM = {
  name: '', email: '', password: '',
  role: 'analyst', default_company_code: '', default_plant: '',
};

export default function ManageUsersPage() {
  const { user, token }     = useAuth();
  const navigate            = useNavigate();
  const [users, setUsers]   = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]     = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]   = useState('');

  // Edit state
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm]     = useState({});
  const [editError, setEditError]   = useState('');
  const [editSaving, setEditSaving] = useState(false);

  // Delete state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting]         = useState(false);

  // Force Reset state
  const [resetTarget,  setResetTarget]  = useState(null);   // user being force-reset
  const [tempPassword, setTempPassword] = useState('');
  const [resetError,   setResetError]   = useState('');
  const [resetting,    setResetting]    = useState(false);
  const [resetDone,    setResetDone]    = useState(false);   // success state in modal

  // Security Settings panel
  const [settingsOpen,  setSettingsOpen]  = useState(false);
  const [resetMode,     setResetModeVal]  = useState(null);  // "admin" | "email" | null
  const [savingMode,    setSavingMode]    = useState(false);
  const [modeError,     setModeError]     = useState('');
  const [modeSaved,     setModeSaved]     = useState(false); // brief "Saved!" flash

  const load = useCallback(() => {
    listUsers(token)
      .then(setUsers)
      .catch(err => setError(err.message));
  }, [token]);

  const loadSettings = useCallback(() => {
    getResetMode(token)
      .then(res => setResetModeVal(res.mode))
      .catch(() => {}); // non-fatal — settings panel just won't pre-populate
  }, [token]);

  useEffect(() => {
    if (user?.role !== 'admin') { navigate('/'); return; }
    load();
    loadSettings();
  }, [user, navigate, load, loadSettings]);

  async function toggleActive(u) {
    try {
      u.is_active ? await deactivateUser(token, u.id)
                  : await activateUser(token, u.id);
      load();
    } catch (err) { alert(err.message); }
  }

  function openEdit(u) {
    setEditTarget(u);
    setEditForm({
      name:                 u.name,
      role:                 u.role,
      default_company_code: u.default_company_code || '',
      default_plant:        u.default_plant || '',
    });
    setEditError('');
  }

  async function handleUpdate(e) {
    e.preventDefault();
    setEditError('');
    setEditSaving(true);
    try {
      await updateUser(token, editTarget.id, {
        name:                 editForm.name || undefined,
        role:                 editForm.role || undefined,
        default_company_code: editForm.default_company_code || undefined,
        default_plant:        editForm.default_plant || undefined,
      });
      setEditTarget(null);
      load();
    } catch (err) {
      setEditError(err.message);
    } finally {
      setEditSaving(false);
    }
  }

  async function handleDelete() {
    setDeleting(true);
    try {
      await deleteUser(token, deleteTarget.id);
      setDeleteTarget(null);
      load();
    } catch (err) {
      alert(err.message);
    } finally {
      setDeleting(false);
    }
  }

  async function handleCreate(e) {
    e.preventDefault();
    setFormError('');
    setSubmitting(true);
    try {
      await createUser(token, form);
      setForm(EMPTY_FORM);
      setShowForm(false);
      load();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  // ── Force Reset ──────────────────────────────────────────────────────────
  function openForceReset(u) {
    setResetTarget(u);
    setTempPassword('');
    setResetError('');
    setResetDone(false);
  }

  async function handleForceReset(e) {
    e.preventDefault();
    setResetError('');
    setResetting(true);
    try {
      await forceResetUserPassword(token, resetTarget.id, tempPassword);
      setResetDone(true);
      load(); // refresh to show must_change_password flag
    } catch (err) {
      setResetError(err.message);
    } finally {
      setResetting(false);
    }
  }

  // ── Reset Mode toggle ────────────────────────────────────────────────────
  async function handleModeChange(newMode) {
    setModeError('');
    setModeSaved(false);
    setSavingMode(true);
    try {
      const res = await setResetMode(token, newMode);
      setResetModeVal(res.mode);
      setModeSaved(true);
      setTimeout(() => setModeSaved(false), 2500);
    } catch (err) {
      setModeError(err.message);
    } finally {
      setSavingMode(false);
    }
  }

  // "email" and null both mean self-service in the UI
  const effectiveMode = resetMode === 'admin' ? 'admin' : 'email';

  return (
    <div className="tenant-page">
      <div className="tenant-page-header">
        <button className="tenant-back-btn" onClick={() => navigate('/')}>
          <ArrowLeft size={15} /> Back to Chat
        </button>
        <h1 className="tenant-page-title">Manage Users</h1>
        <p className="tenant-page-sub">
          {user?.tenant_id} · {users.length} users
        </p>
      </div>

      <div className="tenant-page-body">

        {/* ── Security Settings panel ── */}
        <div className="tenant-settings-panel">
          <button
            className="tenant-settings-header"
            onClick={() => setSettingsOpen(v => !v)}
            aria-expanded={settingsOpen}
          >
            <span className="tenant-settings-title">
              <Settings size={14} /> Security Settings
            </span>
            {settingsOpen ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
          </button>

          {settingsOpen && (
            <div className="tenant-settings-body">
              <div className="tenant-setting-row">
                <div className="tenant-setting-info">
                  <span className="tenant-setting-label">Password Reset Mode</span>
                  <span className="tenant-setting-desc">
                    Controls how users reset their password when locked out.
                  </span>
                </div>
                <div className="tenant-mode-toggle">
                  <button
                    className={`tenant-mode-btn ${effectiveMode === 'admin' ? 'tenant-mode-btn--active' : ''}`}
                    onClick={() => handleModeChange('admin')}
                    disabled={savingMode || effectiveMode === 'admin'}
                    title="Only admins can reset passwords. No self-service."
                  >
                    <ShieldCheck size={13} /> Admin Only
                  </button>
                  <button
                    className={`tenant-mode-btn ${effectiveMode === 'email' ? 'tenant-mode-btn--active' : ''}`}
                    onClick={() => handleModeChange(null)}
                    disabled={savingMode || effectiveMode === 'email'}
                    title="Users receive a 6-digit OTP via email to reset their own password."
                  >
                    <Mail size={13} /> Self-Service (Email OTP)
                  </button>
                </div>
              </div>
              {effectiveMode === 'admin' && (
                <p className="tenant-mode-hint">
                  Users will see "Contact your administrator" on the login screen.
                  Use the <strong>Force Reset</strong> button on each user to set a temporary password.
                </p>
              )}
              {effectiveMode === 'email' && (
                <p className="tenant-mode-hint">
                  Users can reset via email OTP on the login screen.
                  Requires ACS email to be configured — falls back to "Contact admin" if not set up.
                </p>
              )}
              {modeSaved && <p className="tenant-mode-saved">✓ Saved</p>}
              {modeError && <p className="tenant-error">{modeError}</p>}
            </div>
          )}
        </div>

        {/* ── Toolbar ── */}
        <div className="tenant-toolbar">
          <button className="tenant-btn-icon" onClick={load}><RefreshCw size={14} /></button>
          <button className="tenant-btn-primary" onClick={() => setShowForm(true)}>
            <Plus size={14} /> Add User
          </button>
        </div>

        {error && <p className="tenant-error">{error}</p>}

        <div className="tenant-table-wrap">
          <table className="tenant-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Last Login</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && (
                <tr><td colSpan={6} className="tenant-table-empty">No users yet</td></tr>
              )}
              {users.map(u => (
                <tr key={u.id}>
                  <td>
                    {u.name}
                    {u.must_change_password && (
                      <span className="tenant-must-change-badge" title="Must change password on next login">
                        pw reset
                      </span>
                    )}
                  </td>
                  <td>{u.email}</td>
                  <td><span className="tenant-tag">{u.role}</span></td>
                  <td className="tenant-muted">
                    {u.last_login ? new Date(u.last_login).toLocaleDateString('en-IN') : 'Never'}
                  </td>
                  <td>
                    <span className={`tenant-badge ${u.is_active ? 'active' : 'inactive'}`}>
                      {u.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                      {/* Edit */}
                      <button
                        className="tenant-btn-sm"
                        style={{ background: 'var(--clr-bg-elevated)', color: 'var(--clr-text-secondary)', border: '1px solid var(--clr-border)' }}
                        onClick={() => openEdit(u)}
                        title="Edit user"
                      >
                        <Pencil size={12} />
                      </button>

                      {u.email !== user.email && (
                        <>
                          {/* Activate / Deactivate */}
                          <button
                            className={`tenant-btn-sm ${u.is_active ? 'danger' : 'success'}`}
                            onClick={() => toggleActive(u)}
                          >
                            {u.is_active ? 'Deactivate' : 'Activate'}
                          </button>

                          {/* Force Reset password */}
                          <button
                            className="tenant-btn-sm"
                            style={{ background: 'var(--clr-warning-bg)', color: 'var(--clr-warning)', border: '1px solid var(--clr-warning)' }}
                            onClick={() => openForceReset(u)}
                            title="Reset password (admin force)"
                          >
                            <RotateCcw size={11} />
                          </button>

                          {/* Delete */}
                          <button
                            className="tenant-btn-sm danger"
                            onClick={() => setDeleteTarget(u)}
                            title="Delete user"
                          >
                            <Trash2 size={12} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ── Create User Modal ── */}
        {showForm && (
          <div className="tenant-modal-backdrop" onClick={() => setShowForm(false)}>
            <div className="tenant-modal" onClick={e => e.stopPropagation()}>
              <h3 className="tenant-modal-title">Add New User</h3>
              <p style={{ fontSize: 12, color: 'var(--clr-text-secondary)', marginBottom: 14, lineHeight: 1.5 }}>
                The user will be asked to set a new password on their first login.
              </p>
              <form onSubmit={handleCreate} className="tenant-form">
                <div className="tenant-form-row">
                  <label>Name *<input className="tenant-input" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} required /></label>
                  <label>Email *<input type="email" className="tenant-input" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} required /></label>
                </div>
                <div className="tenant-form-row">
                  <label>Temporary Password *<input type="password" className="tenant-input" value={form.password} minLength={6} onChange={e => setForm(f => ({...f, password: e.target.value}))} required /></label>
                  <label>Role *
                    <select className="tenant-input" value={form.role} onChange={e => setForm(f => ({...f, role: e.target.value}))}>
                      <option value="analyst">Analyst</option>
                      <option value="admin">Admin</option>
                    </select>
                  </label>
                </div>
                <div className="tenant-form-row">
                  <label>Company Code<input className="tenant-input" value={form.default_company_code} onChange={e => setForm(f => ({...f, default_company_code: e.target.value}))} placeholder="e.g. 1000" /></label>
                  <label>Plant<input className="tenant-input" value={form.default_plant} onChange={e => setForm(f => ({...f, default_plant: e.target.value}))} placeholder="e.g. 1010" /></label>
                </div>
                {formError && <p className="tenant-error">{formError}</p>}
                <div className="tenant-form-actions">
                  <button type="button" className="tenant-btn-secondary" onClick={() => { setShowForm(false); setForm(EMPTY_FORM); }}>Cancel</button>
                  <button type="submit" className="tenant-btn-primary" disabled={submitting}>{submitting ? 'Creating…' : 'Create User'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ── Edit User Modal ── */}
        {editTarget && (
          <div className="tenant-modal-backdrop" onClick={() => setEditTarget(null)}>
            <div className="tenant-modal" onClick={e => e.stopPropagation()}>
              <h3 className="tenant-modal-title">Edit User — {editTarget.name}</h3>
              <form onSubmit={handleUpdate} className="tenant-form">
                <div className="tenant-form-row">
                  <label>Name *
                    <input className="tenant-input" value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} required />
                  </label>
                  <label>Role *
                    <select className="tenant-input" value={editForm.role} onChange={e => setEditForm(f => ({ ...f, role: e.target.value }))}>
                      <option value="analyst">Analyst</option>
                      <option value="admin">Admin</option>
                    </select>
                  </label>
                </div>
                <div className="tenant-form-row">
                  <label>Company Code
                    <input className="tenant-input" value={editForm.default_company_code} onChange={e => setEditForm(f => ({ ...f, default_company_code: e.target.value }))} placeholder="e.g. 1000" />
                  </label>
                  <label>Plant
                    <input className="tenant-input" value={editForm.default_plant} onChange={e => setEditForm(f => ({ ...f, default_plant: e.target.value }))} placeholder="e.g. 1010" />
                  </label>
                </div>
                {editError && <p className="tenant-error">{editError}</p>}
                <div className="tenant-form-actions">
                  <button type="button" className="tenant-btn-secondary" onClick={() => setEditTarget(null)}>Cancel</button>
                  <button type="submit" className="tenant-btn-primary" disabled={editSaving}>{editSaving ? 'Saving…' : 'Save Changes'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ── Force Reset Modal ── */}
        {resetTarget && (
          <div className="tenant-modal-backdrop" onClick={() => { setResetTarget(null); setResetDone(false); }}>
            <div className="tenant-modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
              {resetDone ? (
                <>
                  <h3 className="tenant-modal-title" style={{ color: 'var(--clr-success)' }}>Password Reset</h3>
                  <p style={{ color: 'var(--clr-text-secondary)', fontSize: '0.9rem', margin: '0 0 1.25rem' }}>
                    Temporary password set for <strong style={{ color: 'var(--clr-text-primary)' }}>{resetTarget.name}</strong>.
                    They will be prompted to change it on next login.
                  </p>
                  <button className="tenant-btn-primary" onClick={() => { setResetTarget(null); setResetDone(false); }}>
                    Done
                  </button>
                </>
              ) : (
                <>
                  <h3 className="tenant-modal-title">
                    <KeyRound size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                    Force Reset — {resetTarget.name}
                  </h3>
                  <p style={{ color: 'var(--clr-text-secondary)', fontSize: '0.875rem', margin: '0 0 1rem' }}>
                    Set a temporary password. The user will be required to change it on their next login.
                  </p>
                  <form onSubmit={handleForceReset} className="tenant-form">
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: '0.8rem', color: 'var(--clr-text-secondary)' }}>
                      Temporary password (min 6 characters)
                      <input
                        type="password"
                        className="tenant-input"
                        value={tempPassword}
                        onChange={e => { setTempPassword(e.target.value); setResetError(''); }}
                        minLength={6}
                        placeholder="Enter a temporary password"
                        autoFocus
                        required
                      />
                    </label>
                    {resetError && <p className="tenant-error">{resetError}</p>}
                    <div className="tenant-form-actions" style={{ marginTop: '1rem' }}>
                      <button type="button" className="tenant-btn-secondary" onClick={() => { setResetTarget(null); setResetDone(false); }}>
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="tenant-btn-primary"
                        style={{ background: 'var(--clr-warning)', borderColor: 'var(--clr-warning)' }}
                        disabled={resetting || tempPassword.length < 6}
                      >
                        {resetting ? 'Resetting…' : 'Set Temporary Password'}
                      </button>
                    </div>
                  </form>
                </>
              )}
            </div>
          </div>
        )}

        {/* ── Delete Confirmation Modal ── */}
        {deleteTarget && (
          <div className="tenant-modal-backdrop" onClick={() => setDeleteTarget(null)}>
            <div className="tenant-modal" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
              <h3 className="tenant-modal-title">Delete User</h3>
              <p style={{ color: 'var(--clr-text-secondary)', fontSize: '0.9rem', margin: '0 0 1.25rem' }}>
                Permanently delete <strong style={{ color: 'var(--clr-text-primary)' }}>{deleteTarget.name}</strong> ({deleteTarget.email})?
                This cannot be undone.
              </p>
              <div className="tenant-form-actions">
                <button className="tenant-btn-secondary" onClick={() => setDeleteTarget(null)}>Cancel</button>
                <button
                  className="tenant-btn-primary"
                  style={{ background: 'var(--clr-error)' }}
                  onClick={handleDelete}
                  disabled={deleting}
                >
                  {deleting ? 'Deleting…' : 'Delete User'}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
